/**
 * ApplyResultDialog — Ergebnis-Dialog nach Bulk-Freigabe (P4/B6a, Mock C,
 * Spec §6.4, T13): öffnet automatisch (Steuerung durch `ReadyBulkList`),
 * zeigt die übernommene Zahl + `outcome`-Wirkungszeilen (`ApplyOutcomeRows`,
 * Task 3/8), Push-Fehler (Reuse `ReadyPushErrorsPanel`) und Undo (Reuse
 * `ReadyUndoAllBar`) wie im inline `ReadyResultPanel` (T8/T13/T14), plus eine
 * „Als Nächstes"-Karte (`ApplyResultNextCard`, T10-Wiederverwendung). Das
 * inline Panel bleibt als Tab-Zustand nach dem Schließen bestehen — dieser
 * Dialog ist nur die einmalige Overlay-Zusammenfassung. Invalidiert beim
 * Öffnen-Übergang (closed→open, per Ref gegated) gezielt den geteilten
 * `['issues-nba']`-Cache, damit frisch geschlossene Issues nicht mitzählen
 * (Spec §6.4 Punkt 5) — ohne den anti-429-Cache anderer Consumer zu treffen.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/v2/ui'
import { useNbaCards } from '@/hooks/useIssuesInsights'
import type { ApplyResult, PushError } from '@/lib/api/channel-content-bulk'
import { EID } from '@/lib/element-ids.registry'

import { useUndoRetryMutations } from '../hooks/useUndoRetryMutations'
import { ApplyOutcomeRows } from './ApplyOutcomeRows'
import { ApplyResultNextCard } from './ApplyResultNextCard'
import { ReadyPushErrorsPanel } from './ReadyPushErrorsPanel'
import { ReadyUndoAllBar } from './ReadyUndoAllBar'

export interface ApplyResultDialogProps {
  result: ApplyResult
  appliedIds: string[]
  channelNameById: Record<string, string>
  worklistTo: string | null
  open: boolean
  onClose: () => void
}

export function ApplyResultDialog({ result, appliedIds, channelNameById, worklistTo, open, onClose }: ApplyResultDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [pushErrors, setPushErrors] = useState<PushError[]>(result.push_errors)
  const [undoResult, setUndoResult] = useState<{ reverted: number; noPrevious: number } | null>(null)
  const { revert } = useUndoRetryMutations()
  const queryClient = useQueryClient()
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) queryClient.invalidateQueries({ queryKey: ['issues-nba'] })
    wasOpenRef.current = open
  }, [open, queryClient])
  const { data: nextCards } = useNbaCards(open)

  const handleUndoAll = () =>
    revert.mutate({ suggestion_ids: appliedIds }, { onSuccess: (r) => setUndoResult({ reverted: r.reverted, noPrevious: r.no_previous }) })

  const handleBack = () => {
    onClose()
    navigate(worklistTo ?? '/v2/tools/text/bulk?tab=runs')
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent id={EID['text-bulk-apply-result-dialog']} aria-live="polite" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('channelContent.resultDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-4xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">{result.applied}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{t('channelContent.resultDialog.appliedBig')}</p>
          </div>

          {result.outcome && <ApplyOutcomeRows outcome={result.outcome} channelNameById={channelNameById} />}
          {pushErrors.length > 0 && <ReadyPushErrorsPanel pushErrors={pushErrors} onResolved={setPushErrors} />}
          <ReadyUndoAllBar busy={revert.isPending} undoResult={undoResult} onUndoAll={handleUndoAll} />

          {nextCards && nextCards.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-xs font-semibold uppercase text-neutral-400">{t('channelContent.resultDialog.nextTag')}</span>
              <ApplyResultNextCard card={nextCards[0]} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button id={EID['text-bulk-apply-result-view-details-btn']} variant="ghost" onClick={onClose}>
            {t('channelContent.resultDialog.viewRunDetails')}
          </Button>
          <Button id={EID['text-bulk-apply-result-back']} data-testid="apply-result-back" onClick={handleBack}>
            {worklistTo ? t('channelContent.resultDialog.backToWorklist') : t('channelContent.resultDialog.toRunsList')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
